import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../utils/supabase';
import { Product, CartItem } from '../types';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [quantities, setQuantities] = useState<{[key: number]: number}>({});

  const { user, signOut } = useAuth();
  const { language, setLanguage, t, isRTL } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchProduct();
      fetchCartItems();
    }
  }, [id]);

  const fetchProduct = async () => {
    const { data } = await supabase
      .from('habib_products')
      .select('*')
      .eq('id', id)
      .single();
    if (data) setProduct(data);
  };

  const fetchCartItems = async () => {
    const { data } = await supabase
      .from('habib_cart')
      .select('*, product:habib_products(*, category:habib_categories(*))')
      .eq('user_id', user?.id);
    if (data) setCartItems(data);
  };

  const updateQuantity = async (productId: number, newQuantity: number) => {
    setQuantities(prev => ({ ...prev, [productId]: newQuantity }));
    
    if (newQuantity === 0) {
      const existingItem = cartItems.find(item => item.product_id === productId);
      if (existingItem) {
        await supabase.from('habib_cart').delete().eq('id', existingItem.id);
        setCartItems(cartItems.filter(item => item.id !== existingItem.id));
      }
      return;
    }
    
    const existingItem = cartItems.find(item => item.product_id === productId);
    
    if (existingItem) {
      const { data } = await supabase
        .from('habib_cart')
        .update({ quantity: newQuantity })
        .eq('id', existingItem.id)
        .select('*, product:habib_products(*, category:habib_categories(*))');
      if (data) {
        setCartItems(cartItems.map(item => 
          item.id === existingItem.id ? data[0] : item
        ));
      }
    } else {
      const { data } = await supabase
        .from('habib_cart')
        .insert([{ user_id: user?.id, product_id: productId, quantity: newQuantity }])
        .select('*, product:habib_products(*, category:habib_categories(*))');
      if (data) setCartItems([...cartItems, ...data]);
    }
  };
  
  const getQuantity = (productId: number) => {
    return quantities[productId] || cartItems.find(item => item.product_id === productId)?.quantity || 0;
  };



  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{borderColor: '#05472A'}}></div>
          <p style={{color: '#05472A'}}>{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 50%, #bbf7d0 100%)'}}>
      <nav className="bg-white/90 backdrop-blur-md shadow-soft" style={{borderBottom: '1px solid #05472A20'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/')}
                className={`${isRTL ? 'ml-4' : 'mr-4'} p-2 rounded-xl hover:bg-green-50 transition-colors`}
                style={{color: '#05472A'}}
              >
                <svg className={`w-6 h-6 ${isRTL ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl ${isRTL ? 'ml-3' : 'mr-3'} flex items-center justify-center shadow-lg overflow-hidden`} style={{backgroundColor: '#05472A'}}>
                <img 
                  src="https://edu.almuwasholah.com/wp-content/uploads/2026/02/logo-habib-1.png"
                  alt="Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="text-lg sm:text-xl font-bold" style={{color: '#05472A'}}>{t('tamuService')}</h1>
            </div>
            <div className={`flex items-center ${isRTL ? 'space-x-reverse' : ''} space-x-2 sm:space-x-4`}>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'en' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'en' ? {backgroundColor: '#05472A'} : {}}
                >
                  EN
                </button>
                <button 
                  onClick={() => setLanguage('ar')}
                  className={`px-2 py-1 rounded text-xs font-medium transition-all duration-300 ${
                    language === 'ar' ? 'text-white' : 'text-gray-600 hover:text-gray-800'
                  }`}
                  style={language === 'ar' ? {backgroundColor: '#05472A'} : {}}
                >
                  AR
                </button>
              </div>
              <span className="hidden md:block text-sm font-medium" style={{color: '#05472A'}}>{t('hi')}, {user?.username}</span>
              <button onClick={signOut} className="bg-white hover:bg-green-50 border px-4 py-2 sm:px-6 sm:py-3 rounded-xl font-semibold transition-all duration-300 shadow-soft hover:shadow-medium" style={{color: '#05472A', borderColor: '#05472A40'}}>
                <span className="hidden sm:inline">{t('signOut')}</span>
                <svg className="w-4 h-4 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Product Image */}
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden border" style={{borderColor: '#05472A20'}}>
            <img 
              src={product.photo} 
              alt={product.nama_produk}
              className="w-full h-96 lg:h-[500px] object-cover"
            />
          </div>

          {/* Product Info */}
          <div className="bg-white rounded-2xl shadow-soft p-6 sm:p-8 border" style={{borderColor: '#05472A20'}}>
            <div className={`flex items-center justify-between mb-4`}>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{color: '#05472A'}}>{product.nama_produk}</h1>
              <span className={`px-4 py-2 rounded-full text-base font-bold ${
                product.stock > 0 
                  ? 'bg-green-100 border' 
                  : 'bg-red-100 text-red-700 border border-red-200'
              }`} style={product.stock > 0 ? {color: '#05472A', borderColor: '#05472A40'} : {}}>
                {product.stock > 0 ? `${product.stock} ${t('left')}` : `${t('outOfStock')}`}
              </span>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3" style={{color: '#05472A'}}>{t('description2')}</h3>
              <p className="text-gray-700 leading-relaxed">{product.deskripsi_produk}</p>
            </div>

            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3" style={{color: '#05472A'}}>{t('productDetails')}</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('productId')}:</span>
                  <span className="font-medium" style={{color: '#05472A'}}>#{product.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('stockAvailable')}:</span>
                  <span className="font-medium" style={{color: '#05472A'}}>{product.stock} {t('units')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('added')}:</span>
                  <span className="font-medium" style={{color: '#05472A'}}>
                    {product.created_at ? new Date(product.created_at).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {product.stock > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3" style={{color: '#05472A'}}>{t('quantity2')}</h3>
                <div className="flex items-center justify-center gap-4">
                  <button 
                    onClick={() => updateQuantity(product.id, Math.max(0, getQuantity(product.id) - 1))}
                    className="w-12 h-12 rounded-xl border-2 flex items-center justify-center hover:bg-green-50 transition-colors"
                    style={{borderColor: '#05472A40', color: '#05472A'}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  </button>
                  <span className="text-2xl font-semibold w-16 text-center" style={{color: '#05472A'}}>
                    {getQuantity(product.id)}
                  </span>
                  <button 
                    onClick={() => updateQuantity(product.id, Math.min(product.stock, getQuantity(product.id) + 1))}
                    className="w-12 h-12 rounded-xl border-2 flex items-center justify-center hover:bg-green-50 transition-colors"
                    style={{borderColor: '#05472A40', color: '#05472A'}}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </main>

      {/* Floating Cart Button */}
      <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
        <button 
          onClick={() => navigate('/cart')} 
          className="text-white px-8 py-4 rounded-full shadow-strong hover:shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95 flex items-center gap-4 min-w-[200px] justify-center"
          style={{backgroundColor: '#05472A'}}
        >
          <div className="relative">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 11-4 0v-6m4 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
            </svg>
            {cartItems.reduce((sum, item) => sum + item.quantity, 0) > 0 && (
              <span className={`absolute -top-2 ${isRTL ? '-left-2' : '-right-2'} bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse`}>
                {cartItems.reduce((sum, item) => sum + item.quantity, 0)}
              </span>
            )}
          </div>
          <span className="font-semibold text-lg">{t('viewCart')}</span>
        </button>
      </div>
    </div>
  );
};

export default ProductDetail;
